import {
  alertingAPI,
  dataEngApi,
  dataPlatformApi,
  nodeAPI,
} from "#components/Services/ServiceHelper";
import { auth0AppClient } from "#src/auth/auth0-app-client";
import { fetchMeasurementTypes, fetchUserProfile } from "#redux/actions";
import { fetchFeatureFlags } from "#redux/actions/featureFlags";
import { fetchUserPermissions } from "#redux/actions/permissions";
import { linkToUserSetting } from "#src/Routers/links";
import { MeasurementType } from "#src/components/Redux/reducers/measurements";
import { AuthenticatedContextAuthVersionType } from "#src/contexts/AuthenticatedContext";
import {
  useAuthenticatedContext,
  useIsFeatureAvailable,
  useIsOpsHubExperience,
} from "#src/contexts/AuthenticatedContext.helpers";
import { HomeRoute } from "#src/routes/home";
import { ExceptionUtils } from "#src/utils/exception";
import { Map, Record } from "immutable";
import React, { ReactElement, useEffect } from "react";
import { connect } from "react-redux";
import { Redirect, useLocation } from "react-router";
import { useAxiosInterceptors } from "./useAxiosInterceptors";
import { FacilitiesListRoute } from "#src/routes/organization/facilities";

const mapStateToProps = ({ measurements }) => {
  return {
    measurements,
  };
};

const mapDispatchToProps = {
  fetchUserProfile,
  fetchUserPermissions,
  fetchMeasurementTypes,
  fetchFeatureFlags,
};

export const AuthenticatedPage = (props: AuthenticatedPageProps) => {
  const { measurements, fetchMeasurementTypes, children } = props;
  const { activeAuthVersion, logout } = useAuthenticatedContext();

  const isV2AuthOnly =
    activeAuthVersion !== "unknown" &&
    Array.isArray(activeAuthVersion) &&
    activeAuthVersion.length === 1 &&
    activeAuthVersion.includes("v2");

  const elixirServiceIsMounting = useAxiosInterceptors(logout, auth0AppClient);
  const nodeServiceIsMounting = useAxiosInterceptors(
    logout,
    auth0AppClient,
    nodeAPI
  );
  const dataEngServiceIsMounting = useAxiosInterceptors(
    logout,
    auth0AppClient,
    dataEngApi
  );
  const alertingServiceIsMounting = useAxiosInterceptors(
    logout,
    auth0AppClient,
    alertingAPI
  );
  const dataPlatformServiceIsMounting = useAxiosInterceptors(
    logout,
    auth0AppClient,
    dataPlatformApi
  );
  const areAxiosInterceptorsMounting = [
    elixirServiceIsMounting,
    nodeServiceIsMounting,
    dataEngServiceIsMounting,
    alertingServiceIsMounting,
    dataPlatformServiceIsMounting,
  ].some((isMounting) => isMounting);

  useEffect(() => {
    if (isV2AuthOnly || measurements.get("measurementTypes").size) return;

    fetchMeasurementTypes();
  }, [isV2AuthOnly, measurements]);

  useEffect(() => {
    if (areAxiosInterceptorsMounting) return;

    ExceptionUtils.registerLifecycleEvent({
      category: "app",
      type: "info",
      message: "Axios interceptors mounted",
    });
  }, [areAxiosInterceptorsMounting]);

  if (areAxiosInterceptorsMounting) {
    return null;
  }

  return (
    <AuthenticatedPageContent {...props}>{children}</AuthenticatedPageContent>
  );
};

export const AuthenticatedPageContent = ({
  children,
}: AuthenticatedPageContentProps) => {
  const location = useLocation();
  const {
    activeAuthVersion,
    isAuthenticated,
    isLoading,
    claims: { user, company },
    v1: { user: legacyUser },
    v2: {
      userInfo: { user: userFull },
    },
  } = useAuthenticatedContext();
  const [isHomeAvailable] = useIsFeatureAvailable({
    featureFlagQuery: "core:home",
  });
  const [isOpsExperienceEnabled] = useIsOpsHubExperience();

  const isV1AuthOnly =
    activeAuthVersion !== "unknown" &&
    Array.isArray(activeAuthVersion) &&
    activeAuthVersion.length === 1 &&
    activeAuthVersion.includes("v1");
  const isDualAuth =
    activeAuthVersion !== "unknown" &&
    Array.isArray(activeAuthVersion) &&
    activeAuthVersion.length === 2 &&
    (["v1", "v2"] satisfies AuthenticatedContextAuthVersionType[]).every((v) =>
      activeAuthVersion.includes(v)
    );
  const isV2AuthOnly =
    activeAuthVersion !== "unknown" &&
    Array.isArray(activeAuthVersion) &&
    activeAuthVersion.length === 1 &&
    activeAuthVersion.includes("v2");
  const isHomePageEnabled =
    ((isDualAuth && !isOpsExperienceEnabled) || isV2AuthOnly) &&
    isHomeAvailable; // the home page is irrelevant to v1 auth users

  useEffect(() => {
    if (!isAuthenticated || isLoading) return;

    const registeredSession = ExceptionUtils.getAuthenticatedSession();

    // don't re-register after critical details have been captured
    if (
      registeredSession.user?.id &&
      registeredSession.tags[ExceptionUtils.ExceptionContextKeys.company] &&
      registeredSession.tags[
        ExceptionUtils.ExceptionContextKeys.activeAuthVersion
      ] !== "unknown"
    )
      return;

    const status = isV1AuthOnly
      ? !legacyUser?.state || legacyUser?.state === "inactive"
        ? "disabled"
        : "active"
      : isDualAuth || isV2AuthOnly
        ? (userFull?.status ?? "disabled")
        : null;
    const timezonePreferred = isV1AuthOnly
      ? legacyUser?.timezone_preference
      : isDualAuth || isV2AuthOnly
        ? userFull?.timezone
        : null;
    const quicksight = isV1AuthOnly
      ? legacyUser?.quicksight
      : isDualAuth || isV2AuthOnly
        ? userFull?.quicksight
        : null;

    ExceptionUtils.registerAuthenticatedSession({
      activeAuthVersion,
      ...(user.id ? { id: user.id, name: user?.name, email: user?.email } : {}),
      ...(company.id ? { company_id: company.id } : {}),
      ...(status !== null ? { status } : {}),
      ...(timezonePreferred !== null ? { timezonePreferred } : {}),
      ...(quicksight !== null ? { quicksight } : {}),
    });
    ExceptionUtils.registerLifecycleEvent({
      category: "session",
      type: "info",
      message: "Registered complete session details",
    });
  }, [
    isLoading,
    isAuthenticated,
    user,
    company,
    legacyUser,
    userFull,
    isV1AuthOnly,
    isDualAuth,
    isV2AuthOnly,
  ]);

  // redirect to the facility list page if the home page feature flag is not
  // enabled and the user is attempting to reach the home page
  if (
    isAuthenticated &&
    !isLoading &&
    !isHomePageEnabled &&
    location.pathname === HomeRoute.path
  ) {
    return (
      <Redirect
        to={
          (isDualAuth && !isOpsExperienceEnabled) || isV2AuthOnly
            ? FacilitiesListRoute.toLink()
            : linkToUserSetting()
        }
      />
    );
  }

  return children ?? null;
};

type AuthenticatedPageProps = {
  measurements: Record<{
    measurementTypes: Map<string, typeof MeasurementType>;
  }>;
  fetchUserProfile: typeof fetchUserProfile;
  fetchUserPermissions: typeof fetchUserPermissions;
  fetchMeasurementTypes: typeof fetchMeasurementTypes;
  fetchFeatureFlags: typeof fetchFeatureFlags;
  children?: ReactElement;
};

type AuthenticatedPageContentProps = {
  children?: ReactElement;
};

// @ts-expect-error TODO: Don't understand the prop difference here
export default connect(mapStateToProps, mapDispatchToProps)(AuthenticatedPage);
